import { CloseSquareOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Checkbox, Col, Form, Input, InputNumber, message, Modal, Row, Space, Spin, Timeline } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { RegionModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import BoxTypeInput from '@/components/FormItems/shipment/BoxTypeInput';

import BooleanTag from '@/components/app/BooleanTag';
import TicketConditionInput from '@/components/FormItems/TicketConditionInput';
import FormItem from 'antd/es/form/FormItem';
import { uniqBy } from 'lodash';
const PRE_LINE = '------------------------------';

const columns: IColumnType<RegionModel>[] = [
  {
    title: 'shipmentManagementPage_IsPercentageBase',
    dataIndex: 'isPercentageBase',
    render: v => <BooleanTag value={v} />,
  },
  {
    title: 'shipmentManagementPage_BeforeAssignTripAmount',
    dataIndex: 'beforeAssignTripAmount',
  },
];

const CancellationPolicyTab = () => {
  const [t] = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    setLoading(true);
    apiSdk.ShipmentCancellationPolicySettingService.getSettings()
      .then(res => {
        form.setFieldsValue(res);
      })
      .finally(() => setLoading(false));
  }, []);
  const updateData = values => {
    setLoading(true);
    apiSdk.ShipmentCancellationPolicySettingService.update({ body: values })
      .then(response => {
        if (response?.success) {
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const hasDuplicateHours = (plans: Array<{ hours: number }>) => {
    plans = plans.filter(x => x && x.hours !== undefined && x.hours !== null);
    let hasDuplicate = uniqBy(plans, x => x.hours).length !== plans.length;
    return hasDuplicate;
  };

  const checkDataBeforeSubmit = (values: any) => {
    if (hasDuplicateHours(values.plans)) {
      Modal.error({
        title: t('Error'),
        content: t('cancellationPolicyPage_DuplicateDaysError'),
      });
      return false;
    }
    return true;
  };
  const isPercentageBase = Form.useWatch(['isPercentageBase'], form);
  return (
    <div style={{ maxWidth: 500 }}>
      <Spin spinning={loading}>
        <Form
          form={form}
          onFinish={values => {
            if (checkDataBeforeSubmit(values)) {
              if (values.id) {
                updateData(values);
              }
            }
          }}
          size="middle"
          layout="vertical"
          initialValues={{
            isPercentageBase: true,
            plans: [{ days: 0, amount: 100 }],
          }}
        >
          <Form.Item name="id" className="hidden">
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={[ROW_GUTTER, ROW_GUTTER]}>
            <Col md={12}>
              <Form.Item
                label={t('shipmentManagementPage_BeforeAssignTripAmount')}
                name="beforeAssignTripAmount"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <InputNumber min={0} className="w-100" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item valuePropName="checked" label={'  '} name="isPercentageBase">
                <Checkbox>{t('cancellationPolicyPage_IsPercentageBase')}</Checkbox>
              </Form.Item>
            </Col>
            <Col md={24}>
              <Form.List name="plans">
                {(fields, { add, remove }) => {
                  return (
                    <Card
                      size="small"
                      title={<b>{t('cancellationPolicyPage_Plans')}</b>}
                      extra={<Button type="primary" onClick={() => add()} icon={<PlusOutlined />}></Button>}
                    >
                      <Timeline mode="left" style={{ marginTop: 20 }}>
                        <Timeline.Item label={<b>{t('cancellationPolicyPage_Hours')}</b>}>
                          <b>{t('cancellationPolicyPage_Amount')}</b>
                        </Timeline.Item>
                        <Timeline.Item label={PRE_LINE}>{PRE_LINE}</Timeline.Item>
                        {fields.map((field, index) => {
                          let hours = parseInt(form.getFieldValue(['plans', field.name, 'hours']) || '0');
                          let isCanNotDelete = hours === 0 && index === 0;
                          let label =
                            hours === 0 && index === 0 ? (
                              'Check in time'
                            ) : (
                              <Space>
                                <Form.Item {...field} name={[field.name, 'hours']} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                                  <InputNumber min={0} addonAfter={t('hours')} addonBefore={t('Before')} style={{ width: 200 }} />
                                </Form.Item>
                              </Space>
                            );
                          return (
                            <Timeline.Item label={label} color={index === 0 ? 'red' : index === 1 ? '#1890ff' : 'gray'}>
                              <div style={{ display: 'flex' }}>
                                <Form.Item {...field} name={[field.name, 'amount']} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                                  <InputNumber style={{ width: 170 }} min={0} addonAfter={isPercentageBase ? '%' : undefined} />
                                </Form.Item>

                                {isCanNotDelete ? null : (
                                  <Button type="link" danger icon={<CloseSquareOutlined onClick={() => remove(field.name)} />}></Button>
                                )}
                              </div>
                            </Timeline.Item>
                          );
                        })}
                      </Timeline>
                    </Card>
                  );
                }}
              </Form.List>
              <FormItem dependencies={['plans']} shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  let plans: Array<{ hours: number }> = getFieldValue(['plans']) || [];
                  let hasDuplicate = hasDuplicateHours(plans);
                  return hasDuplicate ? <div className="error-message">{t('cancellationPolicyPage_DuplicateHoursError')}</div> : null;
                }}
              </FormItem>
            </Col>
            <Col md={24}>
              <Button type="primary" htmlType="submit">
                {t('Save')}
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
};
export default CancellationPolicyTab;
